import { Injectable } from '@angular/core';
import { UsersList } from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store';

export interface ApiLoadingModel {
  getAccounts: {
    isLoading: boolean;
  };
  getSavingsAccounts: {
    isLoading: boolean;
  };
  getDepositAccountInfo: {
    isLoading: boolean;
  };
  getTransactions: {
    isLoading: boolean;
  };
  getCardTransactions: {
    isLoading: boolean;
  };
  getBeneficiaries: {
    isLoading: boolean;
  };
  getBeneficiaryRequiredFields: {
    isLoading: boolean;
  };
  getCardsKycApproval: {
    isLoading: boolean;
  };
  addEditDomesticBeneficiary: {
    isLoading: boolean;
  };
  addInternationalBeneficiary: {
    isLoading: boolean;
  };
  editInternationalBeneficiary: {
    isLoading: boolean;
  };
  approveRejectPaymentRequest: {
    isLoading: boolean;
  };
  savingsInternalTransfer: {
    isLoading: boolean;
  };
  documentsList: {
    isLoading: boolean;
  };
  documentDownload: {
    isLoading: boolean;
  };
  supportedAispInstitutionsLoading: {
    isLoading: boolean;
  };
  getSavingsProduct: {
    isLoading: boolean;
  };
  getSavingsAccountDetails: {
    isLoading: boolean;
  };
  getJointAccountRequests: {
    isLoading: boolean;
  };
}

export enum ApiActions {
  'getAccounts' = 'getAccounts',
  'getSavingsAccounts' = 'getSavingsAccounts',
  'getTransactions' = 'getTransactions',
  'getCardTransactions' = 'getCardTransactions',
  'getBeneficiaries' = 'getBeneficiaries',
  'getBeneficiaryRequiredFields' = 'getBeneficiaryRequiredFields',
  'getCardsKycApproval' = 'getCardsKycApproval',
  'addEditDomesticBeneficiary' = 'addEditDomesticBeneficiary',
  'addInternationalBeneficiary' = 'addInternationalBeneficiary',
  'editInternationalBeneficiary' = 'editInternationalBeneficiary',
  'approveRejectPaymentRequest' = 'approveRejectPaymentRequest',
  'getDepositAccountInfo' = 'getDepositAccountInfo',
  'savingsInternalTransfer' = 'savingsInternalTransfer',
  'documentsList' = 'documentsList',
  'documentDownload' = 'documentDownload',
  'supportedAispInstitutionsLoading' = 'supportedAispInstitutionsLoading',
  'getSavingsProduct' = 'getSavingsProduct',
  'supportedPispInstitutionsLoading' = 'supportedPispInstitutionsLoading',
  'getSavingsAccountDetails' = 'getSavingsAccountDetails',
  'getJointAccountRequests' = 'getJointAccountRequests',
}

export class UpdateApiIsLoadingAction {
  static readonly type = '[ApiLoadingState] UpdateApiAction';
  constructor(public apiAction: ApiActions, public isLoading: boolean) {}
}

@State<ApiLoadingModel>({
  name: 'apiLoading',
  defaults: {
    getAccounts: {
      isLoading: false,
    },
    getSavingsAccounts: {
      isLoading: false,
    },
    getDepositAccountInfo: {
      isLoading: false,
    },
    getTransactions: {
      isLoading: false,
    },
    getCardTransactions: {
      isLoading: false,
    },
    getBeneficiaries: {
      isLoading: false,
    },
    getBeneficiaryRequiredFields: {
      isLoading: false,
    },
    getCardsKycApproval: {
      isLoading: false,
    },
    addEditDomesticBeneficiary: {
      isLoading: false,
    },
    addInternationalBeneficiary: {
      isLoading: false,
    },
    editInternationalBeneficiary: {
      isLoading: false,
    },
    approveRejectPaymentRequest: {
      isLoading: false,
    },
    savingsInternalTransfer: {
      isLoading: false,
    },
    documentsList: {
      isLoading: false,
    },
    documentDownload: {
      isLoading: false,
    },
    supportedAispInstitutionsLoading: {
      isLoading: false,
    },
    getSavingsProduct: {
      isLoading: false,
    },
    getSavingsAccountDetails: {
      isLoading: false,
    },
    getJointAccountRequests: {
      isLoading: false,
    },
  },
})
@Injectable()
export class ApiLoadingState {
  @Selector()
  static getApiLoadingState(state: ApiLoadingModel) {
    return state;
  }
  static getApiState(apiCall: ApiActions) {
    return createSelector([ApiLoadingState], (state: ApiLoadingModel) => {
      return state[apiCall as keyof ApiLoadingModel];
    });
  }

  @Action(UpdateApiIsLoadingAction)
  fetchUsers(ctx: StateContext<UsersList>, action: UpdateApiIsLoadingAction) {
    ctx.patchState({
      [action.apiAction]: {
        isLoading: action.isLoading,
      },
    });
  }

  @Selector([ApiLoadingState])
  static getApiStateAsLoader(state: ApiLoadingModel): boolean {
    const loadingStatesAsLoader = [
      'getCardsKycApproval',
      'addEditDomesticBeneficiary',
      'addInternationalBeneficiary',
      'editInternationalBeneficiary',
      'approveRejectPaymentRequest',
      // Add more Api actions here to show the loading spinner with overlay on the mobile-container
    ] as ApiActions[];

    return loadingStatesAsLoader.some((apiAction) => state[apiAction as keyof ApiLoadingModel].isLoading);
  }
}
